import apiClient from './apiClient'

// ==================== Types ====================

export interface ServiceCategory {
  id: number
  alias: string
  name: string
  icon_url?: string | null
  parent_id?: number | null
  services_count: number
}

export interface ServiceBrief {
  id: number
  alias: string
  name: string
  description?: string | null
  payment_type: string | null
  min_amount: number | null
  max_amount: number | null
  icon_url: string | null
  category_alias?: string | null
  category_name: string | null
  fixed_payment?: boolean
  receive_currency?: string | null
  receive_amount?: number | string | null
}

export interface ServicePage {
  items: ServiceBrief[]
  total: number
  limit: number
  offset: number
}

export interface MPayServiceInput {
  name: string
  required: boolean
  title: string
  regexp?: string
}

export interface Payment2faPolicy {
  mode: 'inherit' | 'enabled' | 'disabled'
  threshold_amount?: number | null
  effective_requires_2fa_for_amount: boolean
  source?: 'service' | 'category' | 'global' | null
}

export interface ServiceDetail {
  id: number
  alias: string
  name: string
  description: string | null
  payment_type: string | null
  min_amount: number | null
  max_amount: number | null
  regex: string | null
  props_name: string | null
  props_description: string | null
  icon_url: string | null
  category_alias?: string | null
  category_name: string | null
  requisite_fields: RequisiteField[] | null
  commissions: Record<string, any> | null
  masks: Record<string, any>[] | null
  // MPay v2.0 specific
  service_type?: number | null
  group?: string | null
  inputs?: MPayServiceInput[] | null
  displays_config?: Record<string, any>[] | null
  fixed_payment?: boolean
  receive_currency?: string | null
  receive_amount?: number | string | null
  country?: string | null
  gateway_api_version?: string | null
  payment_2fa_policy?: Payment2faPolicy | null
  required_fields?: CatalogRequiredField[] | null
}

export interface CatalogRequiredField {
  value?: string
  name?: string
  key?: string
  description_ru?: string
  description_en?: string
  label_ru?: string
  label_en?: string
  label?: string
  required?: boolean
}

export interface RequisiteField {
  name: string
  type: string
  title: string | null
  required: boolean
  priority: number | null
  values: Record<string, any>[] | null
  child: RequisiteField[] | null
}

export interface MPayContract {
  contractId: string
  name?: string
  contractNumber?: string
  contractSum?: string
  contractDate?: string
  currency?: string
  currencyCode?: string
  account?: string
  subscriber?: string
  fullName?: string
}

export interface MPayInvoiceSubService {
  subServiceId: string
  subServiceName: string
  data?: {
    paySum?: number
    tariff?: number
    minSum?: number
    maxSum?: number
    debtInfo?: string
    isMeter?: boolean
    prevCount?: string
    lastCount?: string
    si?: string
    comment?: string
  }
}

export interface MPayInvoice {
  invoiceId?: string
  formedDate?: string
  expireDate?: string
  clientName?: string
  clientAddress?: string
  services?: MPayInvoiceSubService[]
}

export interface RequisiteCheckResult {
  success: boolean
  account_name: string | null
  message: string | null
  detail_code: number | null
  details: string | null
  // MPay three-scheme response fields
  displays?: Record<string, string> | null
  contracts?: MPayContract[] | null
  invoice?: { invoices?: MPayInvoice[] } | null
  service_type?: number | null
  currency_rate?: string | null
  rate?: number | null
  final_amount?: number | null
  provider_currency?: string | null
  fixed_price?: number | null
  receive_amount?: number | null
  receive_currency?: string | null
  fee_amount?: number | null
  total_amount?: number | null
  fee_preview?: Record<string, unknown> | null
  agent_transaction_id?: string | null
  // Provider требует identityCard (паспорт/ИНН) — клиент должен спросить и сохранить
  requires_identity?: boolean | null
  missing_identity_fields?: string[] | null
}

export interface PaymentTransaction {
  id: number
  internal_transaction_id: string
  external_payment_id: number | null
  service_id: number
  service_name: string | null
  requisite: string
  amount: number
  currency: string
  status: string
  is_final: boolean
  result_code: string | null
  detail_code: number | null
  details: string | null
  source?: string | null
  provider?: string | null
  compliance_link?: string | null
  compliance_link_text?: string | null
  payment_method: string
  created_at: string
  updated_at: string
}

export interface PaymentTransactionList {
  total: number
  items: PaymentTransaction[]
}

export interface ExtraServiceItem {
  subServiceId: string
  amount: number
  lastCount?: string
}

export interface PaymentCreateData {
  service_id: number
  requisite: string
  amount: number
  account_id?: number
  field_values?: Record<string, any>
  payer_name?: string
  payer_tax_id?: string
  payer_passport_number?: string
  payer_passport_issued_by?: string
  payer_passport_issued_date?: string
  recipient_name?: string
  extras?: Record<string, any>
  extra_services?: ExtraServiceItem[]
  agent_transaction_id?: string
  totp_code?: string
}

// ==================== QR types ====================

export interface QRGenerateResult {
  resultCode: number
  resultMessage: string
  qrId: string
  qrData: string
  qrImage: string | null
  amount: number
  currency: string
  merchantId: string
  description: string
  expiresAt: string | null
  transaction_id: string | null
  payment_transaction_id: number | null
}

export interface QRProcessResult {
  id: number
  internal_transaction_id: string
  external_payment_id: number | null
  requisite: string
  amount: number
  currency: string
  status: string
  is_final: boolean
  result_code: string | null
  details: string | null
  payment_method: string
  created_at: string
  updated_at: string
}

export interface QRStatusResult {
  transaction_id: string
  status: string
  is_final: boolean
  amount: number | null
  currency: string
  result_code: string | null
  details: string | null
}

// ==================== Payment status helpers ====================

export const FINAL_STATUSES = [
  'CONFIRMED', 'CANCELED', 'ROLLBACK',
  'CREATE_DENIED', 'CONFIRM_DENIED', 'CANCEL_DENIED',
  'ROLLBACK_DENIED', 'EXPIRED',
]

export const STATUS_LABELS: Record<string, string> = {
  NEW: 'Новый',
  CREATED: 'Создан',
  CONFIRMED: 'Подтверждён',
  CANCELED: 'Отменён',
  ROLLBACK: 'Возвращён',
  CREATE_DENIED: 'Отказ при создании',
  CONFIRM_DENIED: 'Отказ при подтверждении',
  CANCEL_DENIED: 'Отказ при отмене',
  ROLLBACK_DENIED: 'Отказ при возврате',
  EXPIRED: 'Истёк срок',
  CONFIRM_IN_PROGRESS: 'Подтверждение...',
  CANCEL_IN_PROGRESS: 'Отмена...',
  ROLLBACK_IN_PROGRESS: 'Возврат...',
  TRY_LATER: 'Повторите позже',
}

export function isSuccessStatus(status: string): boolean {
  return status === 'CONFIRMED'
}

export function isFailedStatus(status: string): boolean {
  return ['CREATE_DENIED', 'CONFIRM_DENIED', 'CANCEL_DENIED', 'ROLLBACK_DENIED', 'EXPIRED'].includes(status)
}

export function isFinalStatus(status: string): boolean {
  return FINAL_STATUSES.includes(status)
}

// ==================== Deposit Method types ====================

export interface DepositMethodInfo {
  key: string
  display_name: string
  description: string | null
  supported_currencies: string[]
  min_amount: number | null
  max_amount: number | null
  fixed_fee: number | null
  percentage_fee: number | null
  priority: number
  maintenance?: boolean
}

export interface MegaPayDeeplinkResult {
  deeplink: string
  amount: number
  destination: string
  currency: string
}

export interface MegaPayRecipientLookup {
  found: boolean
  name: string | null
}

// ==================== Transfer Directions ====================

export interface TransferDirectionService {
  available: boolean
  service_id: number | null
}

export interface RubPayoutDirection {
  available: boolean
  methods: string[]
}

export interface TransferDirections {
  internal: TransferDirectionService
  card: TransferDirectionService
  cross_border: TransferDirectionService
  rub_payout: RubPayoutDirection
}

// ==================== Unified API ====================

const BASE = '/alfabit-payments'

const alfabitPaymentsApi = {
  getDepositMethods: (currency?: string) =>
    apiClient.get<{ methods: DepositMethodInfo[] }>(`${BASE}/deposit-methods`, {
      params: currency ? { currency } : undefined,
    }),

  lookupMegaPayRecipient: (phone: string) =>
    apiClient.get<MegaPayRecipientLookup>(`${BASE}/deposit/megapay-recipient`, {
      params: { phone },
    }),

  createMegaPayDeeplink: (
    data: { account_id?: number; amount: number; recipient_phone?: string },
    idempotencyKey: string,
  ) =>
    apiClient.post<MegaPayDeeplinkResult>(`${BASE}/deposit/megapay-deeplink`, data, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }),

  getTransferDirections: (currency?: string) =>
    apiClient.get<TransferDirections>(`${BASE}/transfer-directions`, {
      params: currency ? { currency } : undefined,
    }),

  getCategories: () =>
    apiClient.get<{ categories: ServiceCategory[] }>(`${BASE}/categories`),

  getServices: (params?: { category?: string; search?: string; limit?: number; offset?: number }) =>
    apiClient.get<ServicePage>(`${BASE}/services`, { params }),

  getServiceDetail: (serviceId: number, amount?: number) =>
    apiClient.get<ServiceDetail>(`${BASE}/services/${serviceId}`, {
      params: amount && amount > 0 ? { amount } : undefined,
    }),

  checkRequisite: (data: { service_id: number; requisite: string; amount?: number; field_values?: Record<string, any> }) =>
    apiClient.post<RequisiteCheckResult>(`${BASE}/check-requisite`, data),

  createPayment: (data: PaymentCreateData) =>
    apiClient.post<PaymentTransaction>(`${BASE}/pay`, data),

  confirmPayment: (transactionId: string) =>
    apiClient.post<PaymentTransaction>(`${BASE}/confirm/${transactionId}`),

  cancelPayment: (transactionId: string) =>
    apiClient.post<PaymentTransaction>(`${BASE}/cancel/${transactionId}`),

  checkPaymentStatus: (transactionId: string) =>
    apiClient.get<PaymentTransaction>(`${BASE}/status/${transactionId}`),

  rollbackPayment: (transactionId: string) =>
    apiClient.post<PaymentTransaction>(`${BASE}/rollback/${transactionId}`),

  getTransactions: (params?: { limit?: number; offset?: number; status?: string }) =>
    apiClient.get<PaymentTransactionList>(`${BASE}/transactions`, { params }),

  syncCatalog: () =>
    apiClient.post<{ gateways_synced: number; total_services: number; errors: string[] }>(`${BASE}/sync-catalog`),

  generateQR: (data: { amount: number; description?: string; currency?: string; account_id?: number }) =>
    apiClient.post<QRGenerateResult>(`${BASE}/qr/generate`, data),

  processQR: (data: { qr_data: string; payer_account?: string; account_id?: number; amount?: number; is_deposit?: boolean }) =>
    apiClient.post<QRProcessResult>(`${BASE}/qr/process`, data),

  checkQRStatus: (transactionId: string) =>
    apiClient.get<QRStatusResult>(`${BASE}/qr/status/${transactionId}`),

  getQRImage: (paymentTransactionId: number) =>
    apiClient.get<{ qr_id: string; qr_image: string | null; status: string; amount: number | null; currency: string }>(`${BASE}/qr/image/${paymentTransactionId}`),
}

export default alfabitPaymentsApi
